import mapJson from 'jsonpath-mapper';
import { Entry } from 'contensis-management-api';

import mapErrorToObject from './mapAppErrorToObject';
import { mapNodesMigrateResult } from './mapNodesMigrateResult';
import ContensisRepository from '~/repositories/ContensisRepository';
import { EntriesMigrationResult } from '~/models';
import ContentAssetsMigrationService from '~/services/ContentAssetsMigrationService';
import EntriesMigrationService from '~/services/EntriesMigrationService';
import { dedupe, dedupeInner, flatten } from '~/util/array';
import { Logger } from '~/util/logger';
import { transformContentTypeId } from '~/util/EntryHelper';

export const mapMigrateResult = (
  service: EntriesMigrationService | ContentAssetsMigrationService,
  repos: { [projectId: string]: ContensisRepository }
): EntriesMigrationResult => {
  Logger.info(`Collating results`, service.source.entryIds?.length > 2000);

  // Get array of keys from target repos
  const projectIds = Object.keys(repos);

  const result = mapJson<typeof service, EntriesMigrationResult>(service, {
    committed: ({ isPreview }: any) => !isPreview,
    query: 'sourceRepo.query',
    zenQL: 'zenQL',
    errors: {
      $path: 'targetRepos.*.entries.migrate[*].error', // are errors bubbled up to service.errors now?
      $formatting: (e) => {
        return e && mapErrorToObject(e);
      },
      $return: (arr) => {
        return dedupeInner(arr.filter((i: any) => i));
      },
      $disable: (arr) => arr.length === 0 || !service.isPreview,
    },
    migrateResult: ({
      isPreview,
      updated,
      created,
      published,
      deleted,
      errors,
    }: any) =>
      !isPreview
        ? {
            updated: updated.length,
            created: created.length,
            published: published.length,
            deleted: deleted.length,
            errors: Object.keys(errors).length > 0 ? errors : undefined,
          }
        : undefined,
    entries: {
      $path: 'sourceRepo.entries.raw[*].sys.contentTypeId',
      $return: (
        contentTypeIds: string[],
        {
          sourceRepo,
        }: {
          sourceRepo: ContensisRepository;
        }
      ) => {
        // if (!sourceRepo.repo?.ProjectId()) return undefined;
        return {
          [sourceRepo.projectId]: {
            ...mapContentTypeEntriesCount(
              contentTypeIds,
              sourceRepo.entries.raw
            ),
            totalCount: sourceRepo.entries.raw.length,
          },
        };
      },
    },
    existing: ({ sourceRepo }: { sourceRepo: ContensisRepository }) => {
      // Get array of keys from target repos
      const projectIds = Object.keys(repos);

      return {
        ...Object.fromEntries(
          projectIds.map((prId) => {
            const targetRepo = repos[prId];
            // Get array of contentTypeIds
            const ctIds = sourceRepo.entries.raw?.map(
              (e) =>
                transformContentTypeId(e, sourceRepo.transformContentTypes).sys
                  .contentTypeId || ''
            );
            return [
              prId,
              {
                ...mapContentTypeEntriesCount(
                  ctIds,
                  targetRepo.entries.migrate
                    ?.map((me) => me.existing)
                    .filter((e) => e) as Entry[]
                ),
                totalCount: targetRepo.entries.raw?.length || 0,
              },
            ];
          })
        ),
      };
    },
    entriesToMigrate: ({
      isPreview,
      sourceRepo,
    }: {
      isPreview: boolean;
      sourceRepo: ContensisRepository;
    }) => {
      let entryIds = {} as {
        [contentTypeId: string]: { [entryId: string]: string };
      };

      (sourceRepo.entryIds?.length
        ? sourceRepo.entryIds
        : (flatten(
            Object.entries(repos)
              .map(([, targetRepo]) => targetRepo.entryIds)
              .filter((r) => r)
          ) as [string, string, string, string][])
      )?.map(([contentTypeId, entryId, entryTitle]) => {
        // Map through every entryId in sourceRepo
        const entryTargets = Object.fromEntries(
          projectIds
            .map((pId) => {
              const targetRepo = repos[pId];
              const migrateEntry = targetRepo.entries.migrate?.find(
                (me) => me.originalId === entryId
              );

              return [
                pId,
                migrateEntry
                  ? {
                      [migrateEntry.id]: {
                        status: migrateEntry.status,
                        diff: isPreview ? migrateEntry.diff : undefined,
                        error:
                          migrateEntry.error?.message || migrateEntry.error,
                      },
                    }
                  : undefined,
              ];
            })
            .filter(([, me]) => me)
        );
        if (Object.keys(entryTargets).length) {
          if (!entryIds[contentTypeId])
            entryIds[contentTypeId] = {
              [entryId]: { entryTitle, ...entryTargets },
            };
          else {
            entryIds[contentTypeId][entryId] = { entryTitle, ...entryTargets };
          }
        }
      });

      entryIds = Object.fromEntries(
        Object.entries(entryIds).sort((a: [string, any], b: [string, any]) =>
          a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0
        )
      );

      // Get array of contentTypeIds
      const contentTypeIds = dedupe(
        sourceRepo.entries.raw?.map((e) => e.sys.contentTypeId) || []
      );

      return {
        ...Object.fromEntries(
          projectIds.map((prId) => {
            const targetRepo = repos[prId];

            // Get array of contentTypeIds
            const ctIds = contentTypeIds.length
              ? contentTypeIds
              : dedupe(
                  targetRepo.entries.raw?.map((e) => e.sys.contentTypeId) || []
                );

            const getCountByStatus = (ctId: string) => {
              const statusEntries = [
                'create',
                'two-pass',
                'update',
                'publish',
                'delete',
                'error',
                'no change',
                'ignore',
              ]
                .map<[string, number]>((status) => [
                  status,
                  targetRepo.entries.migrate?.filter(
                    (me) => me.status === status && me.contentTypeId === ctId
                  ).length || 0,
                ])
                .filter(([, v]) => v > 0);

              return statusEntries.length > 0
                ? Object.fromEntries(statusEntries)
                : undefined;
            };

            return [
              prId,
              {
                ...Object.fromEntries(
                  ctIds
                    .map((ctId) => [ctId, getCountByStatus(ctId || '')])
                    .filter(([, v]) => v)
                    .sort((a: any[], b: any[]) =>
                      a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0
                    )
                ),
                totalCount:
                  targetRepo.entries.migrate?.filter((e) =>
                    [
                      'create',
                      'two-pass',
                      'update',
                      'publish',
                      'delete',
                    ].includes(e.status)
                  )?.length || 0,
              },
            ];
          })
        ),
        entryIds,
      };
    },
  });

  if (service.source.nodes.raw.length > 0) {
    const nodes = mapNodesMigrateResult(service as EntriesMigrationService);
    if (Object.keys(nodes.nodesToMigrate.nodeIds).length) {
      result.nodes = nodes.nodes;
      result.nodesToMigrate = nodes.nodesToMigrate;
      result.nodesResult = nodes.nodesResult;
    }
  }

  if (service.source.tags.raw.length > 0) {
    const tags = service.mapTagsMigrateResult();
    if (Object.keys(tags.tagsToMigrate.tagIds).length) {
      result.tags = tags.tags;
      result.tagsToMigrate = tags.tagsToMigrate;
      result.tagsResult = tags.tagsResult;
    }
  }
  Logger.success(`Results are ready!`);

  // Order the keys in the result object
  return Object.fromEntries(
    (
      [
        'committed',
        'query',
        'zenQL',
        'errors',
        'migrateResult',
        'nodesResult',
        'tagsResult',
        'entries',
        'existing',
        'entriesToMigrate',
        'nodes',
        'nodesToMigrate',
        'tags',
        'tagsToMigrate',
      ] as (keyof typeof result)[]
    ).map((key) => [key, result[key]])
  ) as typeof result;
  // return result;
};
// Map object of { [ctId]: entriesCount }
export const mapContentTypeEntriesCount = (
  contentTypeIds: string[] = [],
  entries: Entry[] = []
) =>
  Object.fromEntries(
    contentTypeIds
      .map((ctId) => [
        ctId,
        entries.filter((e) => e.sys.contentTypeId === ctId).length,
      ])
      .sort((a: any[], b: any[]) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))
  );
